import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Spinner, Modal, Button, Form } from "react-bootstrap";
import toastr from "toastr";
import { ArrowUp, ArrowDown, Search, Pencil, Trash2, Calculator, Download } from "lucide-react";

interface DistroSplitRow {
    id: string;
    distroAccountId: string | null;
    distroName: string | null;
    distroIsActive: boolean;
    adminFeePercent: number;
    ourSplit: number;
    advisorSplit: number;
    ourPortion: number;
    advisorPortion: number;
    label: string | null;
    notes: string | null;
    sortOrder: number | null;
    updatedAt: string | null;
    updatedByName: string | null;
}

interface DistroOption {
    id: string;
    name: string;
}

interface EditState {
    open: boolean;
    isNew: boolean;
    id: string | null;
    distroAccountId: string;
    ourSplit: string;
    advisorSplit: string;
    ourPortion: string;
    advisorPortion: string;
    notes: string;
}

type SortKey =
    | "distroName"
    | "ourSplit"
    | "advisorSplit"
    | "notes";
type SortDir = "asc" | "desc";

const emptyEditState: EditState = {
    open: false,
    isNew: true,
    id: null,
    distroAccountId: "",
    ourSplit: "",
    advisorSplit: "",
    ourPortion: "",
    advisorPortion: "",
    notes: "",
};

const formatPct = (n: number | null | undefined) => {
    if (n === null || n === undefined) return "";
    const fixed = Number(n).toFixed(2);
    return fixed.endsWith(".00") ? fixed.slice(0, -3) : fixed;
};

const parseDecimal = (s: string): number => {
    const n = parseFloat(s);
    return isFinite(n) ? n : 0;
};

const DistroSplitsList: React.FC = () => {
    const [rows, setRows] = useState<DistroSplitRow[]>([]);
    const [distros, setDistros] = useState<DistroOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<string>("ACTIVE");
    const [sortKey, setSortKey] = useState<SortKey>("distroName");
    const [sortDir, setSortDir] = useState<SortDir>("asc");
    const [edit, setEdit] = useState<EditState>(emptyEditState);
    const [saving, setSaving] = useState(false);
    const [pendingDelete, setPendingDelete] = useState<DistroSplitRow | null>(null);
    const [deleting, setDeleting] = useState(false);

    const loadRows = () =>
        axios
            .get("/api/Database/distro-splits")
            .then((res) => {
                const data = (res.data || []).map((x: any) => ({
                    id: x.id,
                    distroAccountId: x.distroAccountId,
                    distroName: x.distroName,
                    distroIsActive: !!x.distroIsActive,
                    adminFeePercent: Number(x.adminFeePercent),
                    ourSplit: Number(x.ourSplit),
                    advisorSplit: Number(x.advisorSplit),
                    ourPortion: Number(x.ourPortion),
                    advisorPortion: Number(x.advisorPortion),
                    label: x.label,
                    notes: x.notes,
                    sortOrder: x.sortOrder,
                    updatedAt: x.updatedAt ?? null,
                    updatedByName: x.updatedByName ?? null,
                }));
                setRows(data);
            })
            .catch(() => {
                toastr.error("Failed to load distro splits", "Error");
            });

    useEffect(() => {
        Promise.all([
            loadRows(),
            axios
                .get("/api/Database/distro-accounts-list")
                .then((res) => setDistros(res.data || []))
                .catch(() => toastr.error("Failed to load distro list", "Error")),
        ]).finally(() => setLoading(false));
    }, []);

    const toggleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortKey(key);
            setSortDir("asc");
        }
    };

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        let list = rows;

        if (statusFilter !== "ALL") {
            const active = statusFilter === "ACTIVE";
            list = list.filter((r) => r.distroIsActive === active);
        }

        if (term) {
            list = list.filter(
                (r) =>
                    (r.distroName || "").toLowerCase().includes(term) ||
                    (r.label || "").toLowerCase().includes(term) ||
                    (r.notes || "").toLowerCase().includes(term)
            );
        }

        const compare = (av: any, bv: any, dir: SortDir) => {
            if (typeof av === "number" && typeof bv === "number") {
                return (av - bv) * (dir === "asc" ? 1 : -1);
            }
            if (typeof av === "boolean" && typeof bv === "boolean") {
                return av === bv ? 0 : (av ? -1 : 1) * (dir === "asc" ? 1 : -1);
            }
            const as = (av ?? "").toString().toLowerCase();
            const bs = (bv ?? "").toString().toLowerCase();
            if (as < bs) return dir === "asc" ? -1 : 1;
            if (as > bs) return dir === "asc" ? 1 : -1;
            return 0;
        };

        const sorted = [...list].sort((a, b) => {
            const primary = compare(a[sortKey], b[sortKey], sortDir);
            if (primary !== 0) return primary;
            if (sortKey !== "distroName") {
                return compare(a.distroName, b.distroName, "asc");
            }
            return compare(a.label, b.label, "asc");
        });
        return sorted;
    }, [rows, search, statusFilter, sortKey, sortDir]);

    const openNew = () => {
        setEdit({ ...emptyEditState, open: true, isNew: true });
    };

    const openEdit = (r: DistroSplitRow) => {
        setEdit({
            open: true,
            isNew: false,
            id: r.id,
            distroAccountId: r.distroAccountId ?? "",
            ourSplit: String(r.ourSplit ?? ""),
            advisorSplit: String(r.advisorSplit ?? ""),
            ourPortion: String(r.ourPortion ?? ""),
            advisorPortion: String(r.advisorPortion ?? ""),
            notes: r.notes ?? "",
        });
    };

    const closeEdit = () => setEdit(emptyEditState);

    // Distros that already have a split row. Excluded from the "Add new"
    // dropdown so ops can't create duplicates. In Edit mode the row's own
    // distro is kept so the current selection stays visible.
    const distroOptions = useMemo(() => {
        const taken = new Set<string>();
        for (const r of rows) {
            if (r.distroAccountId && (edit.isNew || r.id !== edit.id)) {
                taken.add(r.distroAccountId);
            }
        }
        return distros.filter((d) => !taken.has(d.id));
    }, [distros, rows, edit.isNew, edit.id]);

    const csvEscape = (v: string | number | null | undefined) => {
        const s = v === null || v === undefined ? "" : String(v);
        if (/[",\r\n]/.test(s)) {
            return '"' + s.replace(/"/g, '""') + '"';
        }
        return s;
    };

    const exportCsv = () => {
        const headers = ["Distro", "NHC Split (%)", "Advisor Split (%)", "Notes on Split / Total Fee"];
        const lines = [headers.map(csvEscape).join(",")];
        for (const r of filtered) {
            lines.push(
                [r.distroName, formatPct(r.ourSplit), formatPct(r.advisorSplit), r.notes]
                    .map(csvEscape)
                    .join(",")
            );
        }
        const csv = "﻿" + lines.join("\r\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const today = new Date().toISOString().slice(0, 10);
        a.href = url;
        a.download = `distro-splits-${today}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const askDelete = (r: DistroSplitRow) => setPendingDelete(r);

    const cancelDelete = () => {
        if (deleting) return;
        setPendingDelete(null);
    };

    const confirmDelete = () => {
        if (!pendingDelete) return;
        setDeleting(true);
        axios
            .delete(`/api/Database/distro-splits/${pendingDelete.id}`)
            .then(() => {
                toastr.success("Deleted", "Distro Splits");
                setPendingDelete(null);
                return loadRows();
            })
            .catch(() => toastr.error("Delete failed", "Error"))
            .finally(() => setDeleting(false));
    };

    const autoCalcPortions = () => {
        const our = parseDecimal(edit.ourSplit);
        const adv = parseDecimal(edit.advisorSplit);
        const total = our + adv;
        if (total <= 0) {
            toastr.warning("Enter NHC + Advisor split first", "Auto-calc");
            return;
        }
        const ourP = (our / total) * 100;
        const advP = (adv / total) * 100;
        setEdit({
            ...edit,
            ourPortion: ourP.toFixed(2),
            advisorPortion: advP.toFixed(2),
        });
    };

    const save = () => {
        if (!edit.distroAccountId) {
            toastr.error("Select a distro before saving.", "Distro Splits");
            return;
        }

        const required: Array<{ key: keyof EditState; label: string }> = [
            { key: "ourSplit", label: "NHC Split %" },
            { key: "advisorSplit", label: "Advisor Split %" },
            { key: "ourPortion", label: "Our Portion %" },
            { key: "advisorPortion", label: "Advisor Portion %" },
        ];
        for (const { key, label } of required) {
            const raw = (edit[key] as string).trim();
            if (raw === "" || !isFinite(parseFloat(raw))) {
                toastr.error(`${label} is required.`, "Distro Splits");
                return;
            }
        }

        const our = parseDecimal(edit.ourSplit);
        const adv = parseDecimal(edit.advisorSplit);
        const payload = {
            distroAccountId: edit.distroAccountId,
            adminFeePercent: our + adv,
            ourSplit: our,
            advisorSplit: adv,
            ourPortion: parseDecimal(edit.ourPortion),
            advisorPortion: parseDecimal(edit.advisorPortion),
            label: "",
            notes: edit.notes.trim(),
            sortOrder: null as number | null,
        };

        setSaving(true);
        const req = edit.isNew
            ? axios.post("/api/Database/distro-splits", payload)
            : axios.put(`/api/Database/distro-splits/${edit.id}`, payload);

        req.then(() => {
            toastr.success(edit.isNew ? "Created" : "Updated", "Distro Splits");
            closeEdit();
            return loadRows();
        })
            .catch(() => {
                toastr.error("Save failed", "Error");
            })
            .finally(() => setSaving(false));
    };

    const SortHeader = ({
        label,
        column,
        align,
        width,
    }: {
        label: string;
        column: SortKey;
        align?: "left" | "right" | "center";
        width?: number | string;
    }) => (
        <th
            style={{
                cursor: "pointer",
                userSelect: "none",
                textAlign: align ?? "left",
                width,
            }}
            onClick={() => toggleSort(column)}
        >
            {label}
            {sortKey === column && (
                <span style={{ marginLeft: 4 }}>
                    {sortDir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                </span>
            )}
        </th>
    );

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: 40 }}>
                <Spinner animation="border" />
            </div>
        );
    }

    return (
        <div>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 12,
                    flexWrap: "wrap",
                }}
            >
                <h4 style={{ margin: 0, flexGrow: 1 }}>Distro Splits</h4>
                <Button variant="primary" size="sm" onClick={openNew}>
                    Add new
                </Button>
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={exportCsv}
                    disabled={filtered.length === 0}
                    title="Export current view to CSV"
                    style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                >
                    <Download size={14} />
                    CSV
                </Button>
                <div style={{ position: "relative" }}>
                    <Search
                        size={16}
                        style={{ position: "absolute", left: 10, top: 10, color: "#888" }}
                    />
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search distro, label, notes..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ paddingLeft: 32, width: 280 }}
                    />
                </div>
                <select
                    className="form-select"
                    style={{ width: 140 }}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                >
                    <option value="ALL">All statuses</option>
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                </select>
            </div>

            <div className="dashboard-table-container">
                <table className="table table-striped table-hover">
                    <thead>
                        <tr>
                            <SortHeader label="Distro" column="distroName" />
                            <SortHeader label="NHC Split (%)" column="ourSplit" align="right" width={130} />
                            <SortHeader label="Advisor Split (%)" column="advisorSplit" align="right" width={150} />
                            <SortHeader label="Notes on Split / Total Fee" column="notes" />
                            <th style={{ width: 90, textAlign: "center" }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr>
                                <td colSpan={5} style={{ textAlign: "center", padding: 20 }}>
                                    No distro splits match the current filters.
                                </td>
                            </tr>
                        ) : (
                            filtered.map((r) => (
                                <tr key={r.id}>
                                    <td>{r.distroName || <em style={{ color: "#888" }}>Global reference</em>}</td>
                                    <td style={{ textAlign: "right" }}>{formatPct(r.ourSplit)}</td>
                                    <td style={{ textAlign: "right" }}>{formatPct(r.advisorSplit)}</td>
                                    <td style={{ whiteSpace: "pre-wrap" }}>{r.notes}</td>
                                    <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                                        <Button
                                            variant="link"
                                            size="sm"
                                            onClick={() => openEdit(r)}
                                            style={{ padding: "0 4px" }}
                                            title="Edit"
                                        >
                                            <Pencil size={14} />
                                        </Button>
                                        <Button
                                            variant="link"
                                            size="sm"
                                            onClick={() => askDelete(r)}
                                            style={{ padding: "0 4px", color: "#b00020" }}
                                            title="Delete"
                                        >
                                            <Trash2 size={14} />
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            <div style={{ marginTop: 8, color: "#666", fontSize: 13, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                <span>Showing {filtered.length} of {rows.length}</span>
                {(() => {
                    const withStamp = rows
                        .map((r) => ({
                            t: r.updatedAt ? new Date(r.updatedAt).getTime() : NaN,
                            by: r.updatedByName,
                        }))
                        .filter((x) => !isNaN(x.t));
                    if (withStamp.length === 0) return null;
                    const latest = withStamp.reduce((a, b) => (b.t > a.t ? b : a));
                    const when = new Date(latest.t).toLocaleString();
                    const who = latest.by && latest.by.trim() !== "" ? latest.by : "(system)";
                    return (
                        <span>
                            List last updated: {when} by {who}
                        </span>
                    );
                })()}
            </div>

            <Modal show={edit.open} onHide={closeEdit} size="lg" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{edit.isNew ? "Add Distro Split" : "Edit Distro Split"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Distro</Form.Label>
                            <Form.Select
                                value={edit.distroAccountId}
                                onChange={(e) => setEdit({ ...edit, distroAccountId: e.target.value })}
                            >
                                <option value="">(Select a distro)</option>
                                {distroOptions.map((d) => (
                                    <option key={d.id} value={d.id}>
                                        {d.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <div style={{ display: "flex", gap: 12 }}>
                            <Form.Group className="mb-3" style={{ flex: 1 }}>
                                <Form.Label>NHC Split %</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    value={edit.ourSplit}
                                    onChange={(e) => setEdit({ ...edit, ourSplit: e.target.value })}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3" style={{ flex: 1 }}>
                                <Form.Label>Advisor Split %</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    value={edit.advisorSplit}
                                    onChange={(e) => setEdit({ ...edit, advisorSplit: e.target.value })}
                                />
                            </Form.Group>
                        </div>

                        <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
                            <Form.Group className="mb-3" style={{ flex: 1 }}>
                                <Form.Label>Our Portion %</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    value={edit.ourPortion}
                                    onChange={(e) => setEdit({ ...edit, ourPortion: e.target.value })}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3" style={{ flex: 1 }}>
                                <Form.Label>Advisor Portion %</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    value={edit.advisorPortion}
                                    onChange={(e) => setEdit({ ...edit, advisorPortion: e.target.value })}
                                />
                            </Form.Group>
                            <div className="mb-3">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={autoCalcPortions}
                                    title="Auto-calculate portions from Admin Fee + Splits"
                                >
                                    <Calculator size={14} style={{ marginRight: 4 }} />
                                    Auto-calc
                                </Button>
                            </div>
                        </div>

                        <Form.Group className="mb-3">
                            <Form.Label>Notes</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={edit.notes}
                                onChange={(e) => setEdit({ ...edit, notes: e.target.value })}
                                placeholder="Conditional rules, multi-party narrative, etc."
                            />
                        </Form.Group>

                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeEdit} disabled={saving}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={save} disabled={saving}>
                        {saving ? "Saving..." : "Save"}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={pendingDelete !== null} onHide={cancelDelete} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>Delete Distro Split</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {pendingDelete && (
                        <p style={{ margin: 0 }}>
                            Delete the distro split for{" "}
                            <strong>{pendingDelete.distroName || "this row"}</strong>?
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={cancelDelete} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={confirmDelete} disabled={deleting}>
                        {deleting ? "Deleting..." : "Delete"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default DistroSplitsList;
